const TAU = Math.PI * 2;
const rand = (a, b) => a + Math.random() * (b - a);

export const glow = (ctx, x, y, r, color, alpha) => {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r * 1.25);
  g.addColorStop(0, `hsla(${color},${alpha * 0.85})`);
  g.addColorStop(0.3, `hsla(${color},${alpha * 0.6})`);
  g.addColorStop(0.65, `hsla(${color},${alpha * 0.2})`);
  g.addColorStop(1, `hsla(${color},0)`);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r * 1.25, 0, TAU);
  ctx.fill();
};

export const pointer = { x: 0, y: 0, last: -1e9, mode: 1 };
export const rave = { on: false };
export const makeFish = (w, h, varied = false) => {
  const pal = varied ? PALETTE[Math.floor(Math.random() * PALETTE.length)] : [rand(42, 58), 95, 62];
  return {
    x: rand(0, w), y: rand(h * 0.1, h * 0.9),
    size: rand(50, 120), speed: rand(0.15, 0.45) * (Math.random() < 0.5 ? -1 : 1),
    hue: pal[0], sat: pal[1], light: pal[2], ph: rand(0, TAU),
    kind: varied ? KIND_NAMES[Math.floor(Math.random() * KIND_NAMES.length)] : "puff",
  };
};

const PALETTE = [[50, 95, 62], [44, 100, 58], [32, 95, 60], [14, 85, 64], [75, 80, 60], [345, 70, 72], [270, 55, 72], [185, 60, 62]];

const KINDS = {
  puff: { segs: 6, step: 0.35, shrink: 0.12, r: 1.05, wave: 3, tail: 0.6, fins: 0, mouth: 0.55 },
  puffer: { segs: 3, step: 0.22, shrink: 0.1, r: 1.35, wave: 1, tail: 0.35, fins: 0, mouth: 0.75 },
  eel: { segs: 18, step: 0.22, shrink: 0.04, r: 0.6, wave: 5, tail: 0.25, fins: 0, mouth: 0.42 },
  angel: { segs: 4, step: 0.3, shrink: 0.12, r: 0.9, wave: 2, tail: 0.5, fins: 1, mouth: 0.45 },
  moon: { segs: 5, step: 0.28, shrink: 0.1, r: 1.0, wave: 2, tail: 0.75, fins: 0.6, mouth: 0.9 },
};
const KIND_NAMES = Object.keys(KINDS);
KINDS.fatty = { segs: 4, step: 0.26, shrink: 0.06, r: 1.65, wave: 1.2, tail: 0.5, fins: 0.35, mouth: 0.85 };

const SIDES = ["left", "right", "top", "bottom"];

export const spawnFatFish = (w, h) => {
  const side = SIDES[Math.floor(Math.random() * 4)];
  const size = rand(80, 170) * Math.min(1, Math.max(0.45, Math.min(w, h) / 900));
  const m = size * 3;
  const start = {
    left: [-m, rand(0, h)], right: [w + m, rand(0, h)],
    top: [rand(0, w), -m], bottom: [rand(0, w), h + m],
  }[side];
  const tx = w / 2 + rand(-w * 0.6, w * 0.6), ty = h / 2 + rand(-h * 0.6, h * 0.6);
  return {
    x: start[0], y: start[1], angle: Math.atan2(ty - start[1], tx - start[0]),
    v: rand(0.5, 1.2), size, hue: rand(44, 58), sat: 98, light: rand(58, 66), ph: rand(0, TAU), kind: "fatty", speed: 1,
  };
};

export const moveFatFish = (f, w, h, t, k) => {
  f.angle += Math.sin(t * 0.5 + f.ph) * 0.002 * k;
  f.x += Math.cos(f.angle) * f.v * k;
  f.y += Math.sin(f.angle) * f.v * k;
  const m = f.size * 3.5;
  return f.x > -m && f.x < w + m && f.y > -m && f.y < h + m;
};

export const drawFatFish = (ctx, f, t, alpha) => {
  ctx.save();
  ctx.translate(f.x, f.y);
  const flip = Math.cos(f.angle) < 0;
  const shimmy = rave.on ? Math.sin(performance.now() / 1000 * 9 + f.ph) * 0.35 : 0;
  ctx.rotate((flip ? f.angle + Math.PI : f.angle) + shimmy);
  drawFish(ctx, { ...f, x: 0, y: 0, speed: flip ? -1 : 1 }, t, alpha);
  ctx.restore();
};

export const drawFish = (ctx, f, t, alpha) => {
  const dir = Math.sign(f.speed) || 1;
  const s = f.size;
  const K = KINDS[f.kind];
  const dancing = rave.on && f.hue >= 40 && f.hue <= 62;
  const rt = performance.now() / 1000;
  const wt = dancing ? rt * 11 : t * 2.2;
  const amp = dancing ? 4 : 1;
  const col = dancing ? `${Math.floor(rt * 420 + f.ph * 57) % 360},100%,60%` : `${f.hue},${f.sat}%,${f.light}%`;
  if (dancing) alpha *= Math.floor(rt * 2.4) % 2 ? 1.7 : 0.9;
  const wag = Math.sin(wt + f.ph) * (dancing ? 1.6 : 1);
  ctx.globalCompositeOperation = "lighter";
  let tailX = f.x, tailY = f.y;
  for (let k = 0; k < K.segs; k++) {
    tailX = f.x - dir * k * s * K.step;
    tailY = f.y + Math.sin(wt + f.ph - k * 0.6) * k * K.wave * amp + (dancing ? Math.sin(wt * 0.5) * s * 0.08 : 0);
    glow(ctx, tailX, tailY, s * K.r * Math.max(0.2, 1 - k * K.shrink), col, alpha);
  }
  const tx = tailX - dir * s * 0.6;
  glow(ctx, tx, tailY + wag * s * 0.4 - s * 0.32, s * K.tail, col, alpha * 0.85);
  glow(ctx, tx, tailY + wag * s * 0.4 + s * 0.32, s * K.tail, col, alpha * 0.85);
  if (K.fins) {
    const fx = f.x - dir * s * 0.4;
    glow(ctx, fx, f.y - s * (0.9 + wag * 0.1), s * 0.55 * K.fins, `${(f.hue + 20) % 360},${f.sat}%,${f.light + 8}%`, alpha * 0.9);
    glow(ctx, fx, f.y + s * (0.9 + wag * 0.1), s * 0.55 * K.fins, `${(f.hue + 20) % 360},${f.sat}%,${f.light + 8}%`, alpha * 0.9);
  }
  glow(ctx, f.x + dir * s * 0.1, f.y - s * 0.4, s * 0.12, "0,0%,100%", alpha * 1.6);
  const mx = f.x + dir * s * 0.62 * Math.max(1, K.r);
  if (mouthStyle.bubbly) drawBubblyLips(ctx, mx, f.y + s * 0.05, s * K.mouth, Math.min(0.6, alpha * 4.5));
  else drawZip(ctx, mx, f.y + s * 0.05, s * K.mouth, dir, Math.min(0.5, alpha * 3.5));
};

export const mouthStyle = { bubbly: false };

const drawBubblyLips = (ctx, x, y, mr, a) => {
  const w = mr * 0.55, hgt = mr * 0.26;
  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  ctx.filter = `blur(${Math.max(1, mr * 0.05).toFixed(1)}px)`;
  [[-hgt * 0.62, 1], [hgt * 0.62, 1.12]].forEach(([dy, sc]) => {
    const g = ctx.createRadialGradient(x - w * 0.25, y + dy - hgt * 0.3, 0, x, y + dy, w * sc);
    g.addColorStop(0, `rgba(255,205,225,${a})`);
    g.addColorStop(0.55, `rgba(255,110,165,${a * 0.9})`);
    g.addColorStop(1, `rgba(190,40,100,${a * 0.5})`);
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.ellipse(x, y + dy, w * sc, hgt * sc, 0, 0, TAU); ctx.fill();
    ctx.fillStyle = `rgba(255,255,255,${a * 0.7})`;
    ctx.beginPath(); ctx.ellipse(x - w * 0.3, y + dy - hgt * 0.35, w * 0.18, hgt * 0.22, -0.3, 0, TAU); ctx.fill();
  });
  ctx.restore();
  window.__bubblyLips = (window.__bubblyLips || 0) + 1;
};

export const zipFlags = { dawnStart: -1e9 };
const DAWN_MS = 12000;
export const unzipAmount = () => {
  const e = performance.now() - zipFlags.dawnStart;
  if (e < 0 || e > DAWN_MS) return 0;
  return Math.min(1, e / 3000, (DAWN_MS - e) / 3000);
};
export const startDawnUnzip = () => { zipFlags.dawnStart = performance.now(); window.__dawnUnzips = (window.__dawnUnzips || 0) + 1; };

const drawZip = (ctx, x, y, mr, dir, a) => {
  const half = mr * 0.5, u = unzipAmount();
  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  ctx.translate(x, y);
  ctx.filter = `blur(${Math.max(1, mr * 0.05).toFixed(1)}px)`;
  ctx.lineCap = "round";
  ctx.strokeStyle = `rgba(40,26,10,${a * 0.45})`;
  ctx.lineWidth = Math.max(1.5, mr * 0.1);
  ctx.beginPath();
  ctx.moveTo(-half, -half * 0.25 * u);
  ctx.quadraticCurveTo(0, half * 0.9 * u, half, -half * 0.25 * u);
  ctx.stroke();
  ctx.fillStyle = `rgba(225,215,190,${a * 0.35 * (1 - u * 0.7)})`;
  ctx.beginPath(); ctx.ellipse(dir * half, mr * 0.12 - half * 0.25 * u, mr * 0.06, mr * 0.12, 0, 0, TAU); ctx.fill();
  ctx.restore();
  window.__zipMouths = (window.__zipMouths || 0) + 1;
  if (u > 0) window.__zipSmile = u;
};

const steer = (f, k) => {
  if (performance.now() - pointer.last > 4000) return;
  const dx = pointer.x - f.x, dy = pointer.y - f.y;
  const d = Math.hypot(dx, dy) || 1;
  const reach = pointer.mode > 0 ? 1 : Math.max(0, 1 - d / 700);
  if (pointer.mode > 0 && d < f.size) return;
  const pull = (pointer.mode > 0 ? 3 : -9) * k * reach;
  f.x += (dx / d) * pull;
  f.y += (dy / d) * pull;
  const want = Math.sign(dx) * pointer.mode;
  if (reach > 0 && Math.abs(dx) > f.size && Math.sign(f.speed) !== want) f.speed = -f.speed;
};

export const moveFish = (f, w, h, t, k) => {
  f.x += f.speed * k;
  f.y += Math.sin(t * 0.4 + f.ph) * 0.25 * k;
  steer(f, k);
  const m = f.size * 3;
  if (f.x > w + m) f.x = -m;
  if (f.x < -m) f.x = w + m;
  if (f.y > h + m) f.y = -m;
  if (f.y < -m) f.y = h + m;
};

export const createVessels = (w, h) => Array.from({ length: 4 }, () => makeFish(w, h));

export const drawVessels = (ctx, school, w, h, t, p, k) => {
  school.forEach((f) => {
    moveFish(f, w, h, t, k);
    drawFish(ctx, f, t, 0.05 + p * 0.03);
  });
  if (p > 0.01) {
    ctx.globalCompositeOperation = "lighter";
    glow(ctx, w / 2, h / 2, Math.hypot(w, h) * 0.4, `${40 + p * 20},90%,55%`, p * 0.04);
  }
};
